import React, { useMemo } from 'react';
import { Table, Typography, Box, formatPrice } from '@lucid/lego';
import { formatDateTime } from '../../../../../../utils/tools';
import type { PaymentHistoryRecord } from '../types';

type PaymentHistoryProps = {
  theme: 'light' | 'dark';
  data: PaymentHistoryRecord[];
  labels: Record<string, string>;
  locale?: string;
  language?: string;
  dataTestId?: string;
};

/**
 * PaymentHistory component displays a table of payment transactions
 * @component
 * @param {PaymentHistoryProps} props - Component props
 * @returns {JSX.Element} Rendered PaymentHistory component
 *
 * @example
 * ```tsx
 * <PaymentHistory
 *   theme="dark"
 *   data={paymentHistoryData}
 *   labels={labels}
 *   dataTestId="payment-history"
 * />
 * ```
 */
const PaymentHistory: React.FC<PaymentHistoryProps> = ({
  theme,
  data,
  labels,
  locale = 'en-US',
  language,
  dataTestId = 'payment-history',
}) => {
  const columns = useMemo(
    () => [
      {
        key: 'paymentNumber',
        title: labels?.['form.paymentNumber'] ?? '',
        width: '140px',
        sortable: true,
        render: (_: unknown, record: PaymentHistoryRecord) => (
          <Typography variant="body" size="small" color="black">
            {record.paymentNumber || '-'}
          </Typography>
        ),
      },
      {
        key: 'paymentDate',
        title: labels?.['form.paymentDate'] ?? '',
        width: '200px',
        sortable: false,
        render: (_: unknown, record: PaymentHistoryRecord) => (
          <Typography variant="body" size="small" color="black">
            {formatDateTime(record.paymentDate, locale, language)}
          </Typography>
        ),
      },
      {
        key: 'customerOrderAmount',
        title: labels?.['form.customerOrderAmount'] ?? '',
        width: '200px',
        sortable: true,
        render: (_: unknown, record: PaymentHistoryRecord) => (
          <Typography variant="body" size="small" color="black">
            {formatPrice(record.customerOrderAmount, { locale })}
          </Typography>
        ),
      },
      {
        key: 'paymentType',
        title: labels?.['form.paymentType'] ?? '',
        width: '160px',
        sortable: true,
        render: (_: unknown, record: PaymentHistoryRecord) => (
          <Typography variant="body" size="small" color="black">
            {record.paymentType || '-'}
          </Typography>
        ),
      },
      {
        key: 'paymentStatus',
        title: labels?.['form.paymentStatus'] ?? '',
        width: '160px',
        sortable: true,
        render: (_: unknown, record: PaymentHistoryRecord) => {
          const status = record.paymentStatus || 'Pending';
          return (
            <Typography variant="body" size="small" theme={theme} color="black">
              {status}
            </Typography>
          );
        },
      },
    ],
    [theme, labels, locale, language]
  );

  return (
    <>
      <Box mb={16}>
        <Typography variant="body" size="large" theme={theme} color="black">
          {labels?.['form.paymentHistory'] ?? ''}
        </Typography>
      </Box>
      <Box data-testid={dataTestId}>
        <Table
          columns={columns}
          data={data}
          theme={theme === 'dark' ? 'light' : 'dark'}
          selection={{ enabled: false }}
          pagination={{ enabled: false }}
          columnConfig={{ enabled: false }}
          emptyState={{
            message: labels?.['form.noPaymentHistory'] ?? '',
          }}
          gaComponent="payment-history-table"
          compact
          dataTestId="payment-history-table"
        />
      </Box>
    </>
  );
};

PaymentHistory.displayName = 'PaymentHistory';

export default PaymentHistory;
